import { WarningOutlined } from '@ant-design/icons'
import { recordCompact, upsertBy } from '@salescore/buff-common'
import { useCan, useMe } from '@salescore/client-common'
import type { ViewConfigKpiParameter, ViewConfigKpiPreset } from '@salescore/core'
import { SHARED_LINK_PRESET_NAME_INPUT_PATTERN } from '@salescore/core'
import { organizationHasFeature } from '@salescore/frontend-common'
import { Button, Form, Input, Row, Table, Tag } from 'antd'
import { diff as deepObjectDiff } from 'deep-object-diff'
import { Array, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useState } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { kpiParameterAtom, kpiPivotPickedPresetNameAtom } from '../../../recoil/navigation/atoms'
import { configAtom } from '../../../recoil/viewConfig'
import { recursiveRemoveEmptyValues } from './recursive'

export type PresetParameterKeys = keyof ViewConfigKpiPreset['parameter']

/**
 * 選択されたキーだけを表示中の集計条件の値で上書きし、未選択のキーは編集対象プリセットの値をそのまま残す。
 * 未選択キーを初期化しないことが、一覧の鉛筆から名称だけ変えて保存しても他の設定が変わらない根拠になる。
 */
export const buildKpiPresetFromSelection = ({
  name,
  preset,
  parameter,
  selectedKeys,
}: {
  name: string
  preset: ViewConfigKpiPreset | undefined
  parameter: ViewConfigKpiParameter
  selectedKeys: PresetParameterKeys[]
  // eslint-disable-next-line complexity -- キーごとに選択状態で値を振り分けるため、分岐数はキー数で決まる
}): ViewConfigKpiPreset => ({
  name,
  parameter: {
    pivot: selectedKeys.includes(`pivot`)
      ? parameter.pivot
      : {
          rows: preset?.parameter.pivot.rows ?? [],
          columns: preset?.parameter.pivot.columns ?? [],
        },
    queryParameter: selectedKeys.includes(`queryParameter`)
      ? parameter.queryParameter
      : (preset?.parameter.queryParameter ?? undefined),
    invisibleKpiIds: selectedKeys.includes(`invisibleKpiIds`)
      ? parameter.invisibleKpiIds
      : (preset?.parameter.invisibleKpiIds ?? undefined),
    sorter: selectedKeys.includes(`sorter`) ? parameter.sorter : (preset?.parameter.sorter ?? undefined),
    dimensionFilterLeafs: selectedKeys.includes(`dimensionFilterLeafs`)
      ? parameter.dimensionFilterLeafs
      : (preset?.parameter.dimensionFilterLeafs ?? undefined),
    goalConfig: selectedKeys.includes(`goalConfig`)
      ? parameter.goalConfig
      : (preset?.parameter.goalConfig ?? undefined),
    enableHistoricalGroupResolution: selectedKeys.includes('enableHistoricalGroupResolution')
      ? parameter.enableHistoricalGroupResolution
      : (preset?.parameter.enableHistoricalGroupResolution ?? undefined),
  },
})

export const ViewConfigKpiPresetForm = ({
  preset,
  presets,
  initialSelectedKeys,
  onFinish,
}: {
  preset?: ViewConfigKpiPreset
  presets: ViewConfigKpiPreset[] | undefined
  initialSelectedKeys: PresetParameterKeys[]
  onFinish: (newPreset: ViewConfigKpiPreset) => void
}) => {
  const [config] = useRecoilState(configAtom)
  const [parameter] = useRecoilState(kpiParameterAtom)
  const me = useMe()
  const hasAssignmentHistoryFeature = organizationHasFeature(me.organization, 'enable_group_assignment_history')
  const canManageAssignmentHistory = useCan('manage-group-assignment-history')
  const showHistoricalGroupResolution = hasAssignmentHistoryFeature && canManageAssignmentHistory

  const [selectedRowKeys, setSelectedRowKeys] = useState(initialSelectedKeys)

  if (config.type !== 'kpi' && config.type !== 'kpiPivot') {
    // ありえないはず
    return <></>
  }

  const dataSource: Array<{
    label: string
    key: PresetParameterKeys
    content: unknown
  }> = [
    {
      label: t(`行・列`),
      key: `pivot` as const,
      content: parameter.pivot,
    },
    {
      label: t(`期間`),
      key: `queryParameter` as const,
      content: parameter.queryParameter,
    },
    {
      label: t(`絞り込み`),
      key: `dimensionFilterLeafs` as const,
      content: parameter.dimensionFilterLeafs,
    },
    {
      label: t(`目標`),
      key: `goalConfig` as const,
      content: parameter.goalConfig,
    },
    {
      label: t(`非表示KPI設定`),
      key: `invisibleKpiIds` as const,
      content: parameter.invisibleKpiIds,
    },
    {
      label: t(`行を値で並び替え`),
      key: `sorter` as const,
      content: parameter.sorter,
    },
    ...(showHistoricalGroupResolution
      ? [
          {
            label: t('過去所属の反映'),
            key: 'enableHistoricalGroupResolution' as const,
            content: parameter.enableHistoricalGroupResolution,
          },
        ]
      : []),
    // goalConfigはundefinedでも「目標なし」として保存できるように常に表示する
  ].filter((x) => x.content !== undefined || x.key === 'goalConfig')

  return (
    <Form<{ name: string }>
      onFinish={({ name }) => {
        onFinish(buildKpiPresetFromSelection({ name, preset, parameter, selectedKeys: selectedRowKeys }))
      }}
      initialValues={preset}
      layout="vertical"
    >
      <Form.Item
        name="name"
        label={t(`集計プリセット名`)}
        rules={[
          { required: true, message: t('プリセット名を入力してください') },
          {
            pattern: SHARED_LINK_PRESET_NAME_INPUT_PATTERN,
            message: t(`[共有リンク]から始まる文字列をプリセット名に使用することはできません`),
          },
          {
            validator: async (_, presetName: string) => {
              if (presets === undefined || preset?.name === presetName) {
                await Promise.resolve()
              } else {
                const duplicatedCount = presets.filter(({ name }) => name === presetName).length
                if (duplicatedCount > 0) return await Promise.reject(new Error(t('プリセット名が重複しています')))
              }
            },
          },
        ]}
      >
        <Input width={200}></Input>
      </Form.Item>
      <Form.Item name="parameter" label={t(`集計プリセットに含める設定（表示中の集計設定から取り込むもの）`)}>
        <Table
          pagination={false}
          dataSource={dataSource}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (selectedRowKeys) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              setSelectedRowKeys(selectedRowKeys as PresetParameterKeys[])
            },
            getCheckboxProps: (record) => ({
              disabled: false,
              name: record.key,
            }),
          }}
          columns={[
            {
              // title: `設定名`,
              dataIndex: `label`,
              width: 200,
            },
            {
              title: t(`設定内容`),
              width: 500,
              dataIndex: `content`,
              render: (_, record) => getParameterContent(parameter, record.key),
            },
            {
              title: ``,
              width: 500,
              dataIndex: `isOverriding`,

              render: (_, record) => {
                if (preset === undefined) {
                  return <></>
                }

                const oldValue = preset.parameter[record.key] ?? {}
                const newValue = parameter[record.key] ?? {}
                if (!shouldShowOverrideWarning(oldValue, newValue, record.key)) {
                  return <></>
                }

                // 未選択の行は保存しても上書きされないが、どの行にチェックを入れるかの判断材料として、
                // 保存済みの値と表示中の値が違うことは未選択でも示す
                return (
                  <>
                    <Tag color="yellow" icon={<WarningOutlined />}>
                      {selectedRowKeys.includes(record.key)
                        ? t(`下記の設定が上書きされます。`)
                        : t(`未保存の変更があります（チェックすると上書きされます）`)}
                    </Tag>
                    <br />
                    <span className="text-yellow-600">
                      {t(`既存の設定内容`)}: {getParameterContent(preset.parameter, record.key)}
                    </span>
                  </>
                )
              },
            },
          ]}
        />
      </Form.Item>
      <Row justify="end">
        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t(`保存`)}
          </Button>
        </Form.Item>
      </Row>
    </Form>
  )
}

/**
 * 値を持つキーを返す。undefined のキーは除き、空配列・false は値ありとして扱う。
 */
export const getKpiPresetParameterKeysWithValues = (parameter: ViewConfigKpiParameter): PresetParameterKeys[] =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  pipe(parameter, recordCompact, Record.keys) as PresetParameterKeys[]

export const useSetPresetToConfig = () => {
  const setConfig = useSetRecoilState(configAtom)
  const [picked, setPicked] = useRecoilState(kpiPivotPickedPresetNameAtom)

  return (newPreset: ViewConfigKpiPreset, updateTargetPreset?: ViewConfigKpiPreset) => {
    setConfig((oldValue) => {
      if (oldValue.type !== 'kpi' && oldValue.type !== 'kpiPivot') {
        // ありえないが一応
        return oldValue
      }
      // 設定値の見やすさを考慮して、nameをid代わりに使っているが、name自体を書き換えることが可能なので、upsertByでそのまま使えない
      // 一旦keyというプロパティを作ってから後で削除する
      // （考慮する必要が薄いデータの見やすさのために、ロジックが難しくなっているので、素直にidをつけた方が早い気もする。またこれだと同じ名前が二つ作れてしまう）
      const presetsWithKey = (oldValue.presets ?? []).map((x) => ({
        ...x,
        key: x.name,
      }))
      const newPresetWithKey = {
        ...newPreset,
        // keyの一致/不一致でUPSERTしているため、プリセット名が一致する場合は上書きになる。
        // 同名のプリセットを複数保存することは不可。
        key: updateTargetPreset?.name ?? newPreset.name,
      }
      return {
        ...oldValue,
        presets: pipe(
          upsertBy(presetsWithKey, newPresetWithKey, (x) => x.key),
          Array.map((x) => ({
            ...x,
            key: undefined,
          })),
          Array.dedupeWith((a, b) => a.name === b.name),
        ),
      }
    })
    // 現在編集中のプリセットが選択中のプリセットだった場合は表示名を変える必要があるため更新する
    // また新規作成時(presetがundef)は作成するプリセットに切り替える
    if (picked === updateTargetPreset?.name || updateTargetPreset === undefined) {
      setPicked(newPreset.name)
    }
  }
}

// eslint-disable-next-line sonarjs/function-return-type
function getParameterContent(parameter: ViewConfigKpiParameter, key: PresetParameterKeys) {
  switch (key) {
    case 'pivot': {
      const rows = parameter.pivot.rows
        .map((x) => [x.label, x.sortedValues === undefined ? `` : t(`(ソートあり)`)].join(''))
        .join(',')
      const columns = parameter.pivot.columns
        .map((x) => [x.label, x.sortedValues === undefined ? `` : t(`(ソートあり)`)].join(''))
        .join(',')
      return (
        <div>
          {t(`行`)}: {rows}
          <br />
          {t(`列`)}: {columns}
        </div>
      )
    }
    case 'queryParameter': {
      return (
        <div>
          {parameter.queryParameter?.periodName ?? t(`なし`)}
          {parameter.queryParameter?.startAt !== undefined && (
            <>
              <br />
              {t(`開始`)}: {parameter.queryParameter.startAt}
            </>
          )}
          {parameter.queryParameter?.endAt !== undefined && (
            <>
              <br />
              {t(`終了`)}: {parameter.queryParameter.endAt}
            </>
          )}
        </div>
      )
    }
    case 'goalConfig': {
      return <div>{parameter.goalConfig?.name ?? t(`目標なし`)}</div>
    }
    case 'dimensionFilterLeafs': {
      const leafs = parameter.dimensionFilterLeafs ?? []
      if (leafs.length === 0) {
        return t(`なし`)
      }
      const labels = leafs.map((leaf) => {
        if (leaf.type === 'dimension') {
          // この文脈では、必ずdimensionのフィルタが入っているはず？
          return [leaf.dimension.label, leaf.filterValueLabel].filter(isNotNullish).join(': ')
        }
        return leaf.filterValueLabel ?? `フィルタ`
      })
      return <pre>{labels.join('\n')}</pre>
    }
    case 'sorter': {
      const { sorter } = parameter
      if (sorter !== undefined) {
        return t(`あり`) // どの列かを明示するべきだが、やや面倒なので後回し
      }
      return t(`なし`)
    }
    case 'sorterForDrilldownComments': {
      const { sorterForDrilldownComments } = parameter
      if (sorterForDrilldownComments !== undefined) {
        return t(`あり`) // どの列かを明示するべきだが、やや面倒なので後回し
      }
      return t(`なし`)
    }
    case 'invisibleKpiIds': {
      const { invisibleKpiIds } = parameter
      if (invisibleKpiIds !== undefined) {
        return t(`{{length}}件のKPIを非表示`, { length: invisibleKpiIds.length }) // どのKPIかを明示するべきだが、やや面倒なので後回し
      }
      return t(`なし`)
    }
    case 'enableHistoricalGroupResolution': {
      return parameter.enableHistoricalGroupResolution === true ? t('有効') : t('無効')
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      throw new Error(key satisfies never)
    }
  }
}

/**
 * プリセットのパラメータが上書きされるかどうかを判定
 */
export function shouldShowOverrideWarning(oldValue: unknown, newValue: unknown, key: PresetParameterKeys): boolean {
  if (key === 'enableHistoricalGroupResolution') {
    return oldValue !== newValue
  }

  if (key === 'goalConfig') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const { id: oldId } = (oldValue ?? {}) as { id: string | undefined }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const { id: newId } = (newValue ?? {}) as { id: string | undefined }

    if (oldId === newId) {
      return false
    }
    // goalConfigの場合は、newValue === {}が「目標なし」を表すため差分判定を行う
    if (Object.keys(newValue ?? {}).length === 0 && oldId !== undefined) {
      return true
    }
  }

  // 差分からundefined/null/空arrayを除外する
  const diff = recursiveRemoveEmptyValues(deepObjectDiff(oldValue ?? {}, newValue ?? {}))

  return Object.keys(diff).length > 0
}
